import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Toast,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { start } from 'repl';
import {
  DataTable,
  DataTableItem,
  DataTableItemAction,
  HeaderWithOrderBy,
  Loader,
  PaginationComponent,
  SearchBarFilters,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  CustomerFeedBackListOrderByDirectionOptions,
  CustomerFeedBackListOrderByOptions,
  FeedbacksFilter,
  getFeedbacksAsync,
  isValidFeedbackState,
  selectFeedbackItems,
  selectFeedbackItemsLoadingStatus,
  selectFeedbackPagination,
} from './FeedbacksSlice';
import { LOADING_STATUS } from '../../common/constants';
import { InitialPaginationParameters } from '../../common/types/pagination.types';
import { JsDateToString } from '../../common/utils/dateUtils';
import CabinQuickSearchFieldOnly, {
  AccommodationOptionType,
} from '../../common/components/CabinQuickSearch/CabinQuickSearchFieldOnly';
import OwnerQuickSearch, {
  OwnerOptionType,
} from '../../common/components/OwnerQuickSearch/OwnerQuickSearch';
import { getN8nApprovalDecision, N8nApprovalDecision } from './FeedbacksAPI';

interface DateRange {
  from: Date;
  to: Date;
}
function Feedbacks() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const feedbackItems = useAppSelector(selectFeedbackItems);
  const feedbackItemsPagination = useAppSelector(selectFeedbackPagination);
  const feedbackItemsLoadingStatus = useAppSelector(
    selectFeedbackItemsLoadingStatus,
  );
  const [selectedOwnerOptionType, setSelectedOwnerOptionType] =
    useState<OwnerOptionType | null>(null);
  const [selectedAccommodationOptionType, setSelectedAccommodationOptionType] =
    useState<AccommodationOptionType | null>(null);
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  // Create a date object for tomorrow
  // const tomorrow = new Date();
  // tomorrow.setDate(today.getDate() + 1);
  const [dayPickerSelectedRange, setDayPickerSelectedRange] =
    useState<DateRange>({
      from: oneMonthAgo,
      to: today,
    });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const initialFeedbacksFilter: FeedbacksFilter = {
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: InitialPaginationParameters.PageSize,
    feedbackState: 'New',
    ownerId: null,
    accommodationId: null,
    orderBy: CustomerFeedBackListOrderByOptions.AnswerTime,
    FeedbackFetchAnswerStartDate: null,
    FeedbackFetchAnswerEndDate: null,
    OrderDirection: CustomerFeedBackListOrderByDirectionOptions.Descending,
    handlerName: null,
  };

  const [feedbacksFilter, setFeedbacksFilter] = useState(
    initialFeedbacksFilter,
  );

  const [feedbackTableItems, setFeedbackTableItems] = useState<DataTableItem[]>(
    [],
  );
  const [showN8nModal, setShowN8nModal] = useState(false);
  const [n8nDecision, setN8nDecision] = useState<N8nApprovalDecision | null>(
    null,
  );
  const [n8nLoading, setN8nLoading] = useState(false);
  const [n8nError, setN8nError] = useState<string | null>(null);

  const handleSelectAccommodationChange = (
    selected: AccommodationOptionType | null,
  ) => {
    setSelectedAccommodationOptionType(selected);
    setFeedbacksFilter({
      ...feedbacksFilter,
      accommodationId: selected ? selected.value : null,
    });
  };
  const handleSelectOwnerChange = (ownerOption: OwnerOptionType | null) => {
    setSelectedOwnerOptionType(ownerOption);
    setFeedbacksFilter({
      ...feedbacksFilter,
      ownerId: ownerOption ? ownerOption.value : null,
    });
  };

  const handleFeedbackStateSelectChange = event => {
    const { target } = event;
    const { value, name } = target;

    setFeedbacksFilter({
      ...feedbacksFilter,
      [name]: value === '' ? null : value,
    });
  };

  const handleGetN8nApprovalDecision = async (feedbackId: string) => {
    setN8nLoading(true);
    setN8nError(null);
    try {
      const decision = await getN8nApprovalDecision(feedbackId);
      await dispatch(getFeedbacksAsync(feedbacksFilter));
      setN8nDecision(decision);
      setShowN8nModal(true);
    } catch (error) {
      setN8nError(
        error instanceof Error
          ? error.message
          : 'Failed to get approval decision',
      );
      setShowN8nModal(true);
    } finally {
      setN8nLoading(false);
    }
  };

  const setDatePickerDropdownDateRange = (
    startDateSelected: Date | null | undefined,
    endDateSelected: Date | null | undefined,
  ) => {
    setFeedbacksFilter(prev => {
      let newStartDate = prev.FeedbackFetchAnswerStartDate;
      let newEndDate = prev.FeedbackFetchAnswerEndDate;

      if (startDateSelected !== undefined) {
        newStartDate = startDateSelected;
        if (newEndDate && startDateSelected && newEndDate < startDateSelected) {
          newEndDate = startDateSelected;
        }
      }

      if (endDateSelected !== undefined) {
        newEndDate = endDateSelected;
      }

      return {
        ...prev,
        FeedbackFetchAnswerStartDate: newStartDate,
        FeedbackFetchAnswerEndDate: newEndDate,
      };
    });
  };

  const doSearch = () => {
    const newParams: Record<string, string> = {};
    const searchFilters: FeedbacksFilter = feedbacksFilter;

    // ✅ Use values directly from feedbacksFilter, which are up-to-date
    if (feedbacksFilter.FeedbackFetchAnswerStartDate) {
      newParams.FeedbackFetchAnswerStartDate =
        feedbacksFilter.FeedbackFetchAnswerStartDate.toISOString();
    }

    if (feedbacksFilter.FeedbackFetchAnswerEndDate) {
      newParams.FeedbackFetchAnswerEndDate =
        feedbacksFilter.FeedbackFetchAnswerEndDate.toISOString();
    }

    if (feedbacksFilter.ownerId?.trim()) {
      newParams.ownerId = feedbacksFilter.ownerId;
      newParams.ownerOptionLabel = selectedOwnerOptionType.label;
    }

    if (feedbacksFilter.accommodationId?.trim()) {
      newParams.accommodationId = feedbacksFilter.accommodationId;
      newParams.accommodationOptionLabel =
        selectedAccommodationOptionType.label;
    }
    // console.log(feedbacksFilter.feedbackState);
    if (feedbacksFilter.feedbackState != null) {
      newParams.feedbackState = feedbacksFilter.feedbackState.toString();
    } else {
      newParams.feedbackState = null;
    }
    /*
    if (
      newParams.feedbackState !== 'New' &&
      (!feedbacksFilter.ownerId || feedbacksFilter.ownerId === '') &&
      (!feedbacksFilter.accommodationId ||
        feedbacksFilter.accommodationId === '')
    ) {
      alert(
        'Valitse joko kohde tai omistaja hakeaksesi palautteita muilla tiloilla paitsi UUSI',
      );
      return;
    }
*/
    if (
      newParams.feedbackState === '' &&
      !feedbacksFilter.FeedbackFetchAnswerStartDate &&
      !feedbacksFilter.FeedbackFetchAnswerEndDate
    ) {
      alert('Valitse ajankohta, jos Tilaa ei ole valittu');
      return;
    }
    if (feedbacksFilter.orderBy != null) {
      newParams.orderBy =
        CustomerFeedBackListOrderByOptions[feedbacksFilter.orderBy];
    }

    if (feedbacksFilter.OrderDirection != null) {
      newParams.orderDirection =
        CustomerFeedBackListOrderByDirectionOptions[
          feedbacksFilter.OrderDirection
        ];
    }

    // Update URL
    setSearchParams(newParams);

    // Trigger search
    dispatch(getFeedbacksAsync(searchFilters));
  };

  const doSearchWitParams = (orderBy, orderDirection) => {
    // Create a new filter object so you don't mutate state directly
    const searchFilters: FeedbacksFilter = {
      ...feedbacksFilter,
      orderBy,
      OrderDirection: orderDirection,
      FeedbackFetchAnswerStartDate:
        startDate ?? feedbacksFilter.FeedbackFetchAnswerStartDate,
      FeedbackFetchAnswerEndDate:
        endDate ?? feedbacksFilter.FeedbackFetchAnswerEndDate,
    };

    // Update React state so UI updates
    setFeedbacksFilter(searchFilters);

    // Update the URL search params (assuming you have access to navigate and location)
    const newSearchParams = new URLSearchParams(location.search);

    // Update or set the filter params
    if (searchFilters.ownerId) {
      newSearchParams.set('ownerId', searchFilters.ownerId);
    } else {
      newSearchParams.delete('ownerId');
    }

    if (searchFilters.accommodationId) {
      newSearchParams.set('accommodationId', searchFilters.accommodationId);
    } else {
      newSearchParams.delete('accommodationId');
    }

    if (searchFilters.FeedbackFetchAnswerStartDate) {
      newSearchParams.set(
        'FeedbackFetchAnswerStartDate',
        searchFilters.FeedbackFetchAnswerStartDate.toISOString().substring(
          0,
          10,
        ),
      );
    } else {
      newSearchParams.delete('FeedbackFetchAnswerStartDate');
    }

    if (searchFilters.FeedbackFetchAnswerEndDate) {
      newSearchParams.set(
        'FeedbackFetchAnswerEndDate',
        searchFilters.FeedbackFetchAnswerEndDate.toISOString().substring(0, 10),
      );
    } else {
      newSearchParams.delete('FeedbackFetchAnswerEndDate');
    }
    console.log(searchFilters.feedbackState);
    if (searchFilters.feedbackState) {
      newSearchParams.set('feedbackState', searchFilters.feedbackState);
    } else {
      newSearchParams.delete('feedbackState');
    }

    // Set enums as strings
    newSearchParams.set(
      'orderBy',
      CustomerFeedBackListOrderByOptions[searchFilters.orderBy],
    );
    newSearchParams.set(
      'orderDirection',
      CustomerFeedBackListOrderByDirectionOptions[searchFilters.OrderDirection],
    );

    // Push the new URL with updated search params
    navigate(`?${newSearchParams.toString()}`, { replace: true });

    // Finally dispatch the search action
    dispatch(getFeedbacksAsync(searchFilters));
  };

  const HandlePaginationClick = (Page: number) => {
    const paginatedFiltering = feedbacksFilter;
    /*
    if (startDate !== undefined) {
      feedbacksFilter.FeedbackFetchAnswerStartDate = startDate;
    }
    if (endDate !== undefined) {
      feedbacksFilter.FeedbackFetchAnswerEndDate = endDate;
    }
     */
    paginatedFiltering.PageNumber = Page;
    setFeedbacksFilter(paginatedFiltering);
    // console.log(paginatedFiltering);
    dispatch(getFeedbacksAsync(paginatedFiltering));
  };
  useEffect(() => {
    const searchedOwnerId = searchParams.get('ownerId');
    const searchedOwnerOptionLabel = searchParams.get('ownerOptionLabel');
    const searchedAccommodationId = searchParams.get('accommodationId');
    const searchedAccommodationOptionLabel = searchParams.get(
      'accommodationOptionLabel',
    );
    const searchedStartDate = searchParams.get('FeedbackFetchAnswerStartDate');
    const searchedEndDate = searchParams.get('FeedbackFetchAnswerEndDate');
    const searchedFeedbackState = searchParams.get('feedbackState');
    const orderByString = searchParams.get('orderBy');
    const orderDirectionString = searchParams.get('orderDirection');
    // if (searchedOwnerId != null || searchedAccommodationId != null) {
    let { orderBy, OrderDirection } = feedbacksFilter;
    if (orderByString != null) {
      orderBy =
        CustomerFeedBackListOrderByOptions[
          orderByString as keyof typeof CustomerFeedBackListOrderByOptions
        ];
    }
    if (orderDirectionString != null) {
      OrderDirection =
        CustomerFeedBackListOrderByDirectionOptions[
          orderDirectionString as keyof typeof CustomerFeedBackListOrderByDirectionOptions
        ];
    }
    setFeedbacksFilter(prev => ({
      ...prev,
      ownerId: searchedOwnerId,
      accommodationId: searchedAccommodationId,
      feedbackState: isValidFeedbackState(searchedFeedbackState)
        ? searchedFeedbackState
        : 'New',
      FeedbackFetchAnswerStartDate: searchedStartDate
        ? new Date(searchedStartDate)
        : null,
      FeedbackFetchAnswerEndDate: searchedEndDate
        ? new Date(searchedEndDate)
        : null,
      orderBy,
      OrderDirection,
    }));
    if (searchedOwnerId != null) {
      setSelectedOwnerOptionType({
        value: searchedOwnerId,
        label: searchedOwnerOptionLabel,
      });
    }
    if (searchedAccommodationId != null) {
      setSelectedAccommodationOptionType({
        value: searchedAccommodationId,
        label: searchedAccommodationOptionLabel,
      });
    }
    // }

    if (feedbackItems == null || feedbackItems.length === 0) {
      doSearch();
    }
  }, []);

  useEffect(() => {
    if (feedbackItems && feedbackItems.length > 0) {
      const formattedFeedbackList: DataTableItem[] = [];
      feedbackItems?.forEach(item => {
        let autoApprovalStatus = '';
        if (item.autoApprove !== undefined && item.autoApprove !== null) {
          const decision = item.autoApprove ? 'Kyllä' : 'Ei';
          autoApprovalStatus = `${decision}`;
        }
        formattedFeedbackList.push({
          ItemId: item.id,
          Items: [
            JsDateToString(item.sentTime, true, true, true, ' ', true, true),
            JsDateToString(item.answerTime, true, true, true, ' ', true, true),
            item.contactRequested
              ? `${item.feedbackState} YHTEYDENOTTOPYYNTÖ`
              : item.feedbackState,
            `${item.customerFirstName} ${item.customerLastName}`,
            `${item.bookingNumber.toString()}`,
            `${JsDateToString(
              item.bookingStartTime,
              true,
              true,
              true,
              '',
              false,
              false,
            )} - ${JsDateToString(
              item.bookingEndTime,
              true,
              true,
              true,
              '',
              false,
              false,
            )}`,
            `${item.accommodationNumber.toString()}`,
            `${item.ownerFirstName} ${item.ownerLastName}`,
            // 'TODO: SITE',
            // item.locale,
            JsDateToString(
              item.approvedRejectedTime,
              true,
              true,
              true,
              ' ',
              true,
              true,
            ),
            JsDateToString(
              item.ownerReplySentTime,
              true,
              true,
              true,
              ' ',
              true,
              true,
            ),
            item.feedbackHandlerFirstName &&
              item.feedbackHandlerLastName &&
              `${item.feedbackHandlerFirstName} ${item.feedbackHandlerLastName}`,
            autoApprovalStatus,
          ],
          Url: `/feedbacks/details/${item.id}`,
        });
      });
      setFeedbackTableItems(formattedFeedbackList);
    }
  }, [feedbackItems]);
  const HeaderWithOrderByLinks: HeaderWithOrderBy[] = [
    { text: 'Lähetetty', OrderByField: 'SentTime' },
    { text: 'Vastattu', OrderByField: 'AnswerTime' },
    { text: 'Tila', OrderByField: 'FeedbackState' },
    { text: 'Asiakas', OrderByField: 'CustomerLastName' },
    { text: 'Varaus', OrderByField: 'BookingNumber' },
    { text: 'Aika', OrderByField: 'BookingStartTime' },
    { text: 'Kohde', OrderByField: 'AccommodationNumber' },
    { text: 'Omistaja', OrderByField: 'OwnerLastName' },
    { text: 'Ok / Ei Ok' },
    { text: 'Lähetetty omistajalle' },
    { text: 'Käsittelijä', OrderByField: 'FeedbackHandler' },
    { text: 'N8n Päätös' },
    { text: 'Toiminnot' },
  ];
  const OnHeaderClick = (orderByField: string) => {
    let newOrderBy: CustomerFeedBackListOrderByOptions =
      CustomerFeedBackListOrderByOptions.AnswerTime;
    if (orderByField === 'SentTime') {
      newOrderBy = CustomerFeedBackListOrderByOptions.SentTime;
    }
    if (orderByField === 'AnswerTime') {
      newOrderBy = CustomerFeedBackListOrderByOptions.AnswerTime;
    }
    if (orderByField === 'FeedbackState') {
      newOrderBy = CustomerFeedBackListOrderByOptions.FeedbackState;
    }
    if (orderByField === 'CustomerLastName') {
      newOrderBy = CustomerFeedBackListOrderByOptions.CustomerLastName;
    }
    if (orderByField === 'BookingNumber') {
      newOrderBy = CustomerFeedBackListOrderByOptions.BookingNumber;
    }
    if (orderByField === 'AccommodationNumber') {
      newOrderBy = CustomerFeedBackListOrderByOptions.AccommodationNumber;
    }
    if (orderByField === 'OwnerLastName') {
      newOrderBy = CustomerFeedBackListOrderByOptions.OwnerLastName;
    }
    if (orderByField === 'BookingStartTime') {
      newOrderBy = CustomerFeedBackListOrderByOptions.BookingStartTime;
    }
    if (orderByField === 'FeedbackHandler') {
      newOrderBy = CustomerFeedBackListOrderByOptions.FeedbackHandler;
    }
    let direction =
      feedbacksFilter.OrderDirection ===
      CustomerFeedBackListOrderByDirectionOptions.Ascending
        ? CustomerFeedBackListOrderByDirectionOptions.Descending
        : CustomerFeedBackListOrderByDirectionOptions.Ascending;

    if (newOrderBy === feedbacksFilter.orderBy) {
      // clicked the column that already is the order by
      // so just change direction
      console.log(`feedbacksFilter setting new direction: ${direction}`);
      setFeedbacksFilter({ ...feedbacksFilter, OrderDirection: direction });
    } else {
      // Clicked column that is NOT already selected order by
      direction = CustomerFeedBackListOrderByDirectionOptions.Ascending;
      setFeedbacksFilter({
        ...feedbacksFilter,
        orderBy: newOrderBy,
        OrderDirection: CustomerFeedBackListOrderByDirectionOptions.Ascending,
      });
    }
    doSearchWitParams(newOrderBy, direction);
  };

  const renderItemActions = (itemId: string): DataTableItemAction[] => [
    {
      ButtonText: n8nLoading ? 'Ladataan...' : 'Hae päätös',
      ButtonAction: () => handleGetN8nApprovalDecision(itemId),
    },
  ];
  return (
    <>
      <Helmet>
        <title>Palautteet - Haku</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader Breadcrumbs={['Palautteet']} Title="Palautteiden haku" />

        <Card className="bg-white border shadow-none p-24 pb-8 mb-40">
          <SearchBarFilters DisplayOnDesktop FullWidth IsLoading={false}>
            <Row>
              <Col sm={6}>
                <Form.Group
                  controlId="feedbackStateFilterSelect"
                  className="mb-24"
                >
                  <Form.Label>Kohde</Form.Label>
                  <CabinQuickSearchFieldOnly
                    selectedAccommodation={selectedAccommodationOptionType}
                    Placeholder="Hae kohde"
                    parentHandleSelectAccommodationChange={
                      handleSelectAccommodationChange
                    }
                  />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group
                  controlId="feedbackStateFilterSelect"
                  className="mb-24"
                >
                  <Form.Label>Omistaja</Form.Label>
                  <OwnerQuickSearch
                    selectedOwnerOptionType={selectedOwnerOptionType}
                    Placeholder="Hae omistaja"
                    parentHandleSelectOwnerChange={handleSelectOwnerChange}
                  />
                </Form.Group>
                <Form.Group controlId="feedbackHandlerFilter" className="mb-24">
                  <Form.Label>Käsittelijä</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Hae käsittelijän nimellä"
                    value={feedbacksFilter.handlerName ?? ''}
                    onChange={e =>
                      setFeedbacksFilter({
                        ...feedbacksFilter,
                        handlerName: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="feedbackStateFilterSelect">
                  <Form.Label>Palautteen tila</Form.Label>
                  <Form.Select
                    id="feedbackStateSelect"
                    value={feedbacksFilter.feedbackState ?? ''} // <- controlled value
                    onChange={e => {
                      handleFeedbackStateSelectChange(e);
                    }}
                    name="feedbackState"
                  >
                    <option value="">Ei tila valintaa</option>
                    <option value="New">Uusi</option>
                    {/* }<option value="InProgress">Käsittelyssä</option>{ */}
                    <option value="Approved">Hyväksytty</option>
                    <option value="PartiallyApproved">Osittain hylätty</option>
                    <option value="Rejected">Hylätty</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Label htmlFor="selectStart">
                  Varauksen ajankohta
                </Form.Label>
                <div className="d-flex  gap-8 align-items-center">
                  <div className="w-100 position-relative">
                    <DatePicker
                      locale={fi}
                      selected={
                        feedbacksFilter.FeedbackFetchAnswerStartDate ?? null
                      }
                      onChange={date =>
                        setDatePickerDropdownDateRange(date ?? null, undefined)
                      }
                      selectsStart
                      startDate={
                        feedbacksFilter.FeedbackFetchAnswerStartDate ?? null
                      }
                      endDate={
                        feedbacksFilter.FeedbackFetchAnswerEndDate ?? null
                      }
                      className="form-control"
                      dateFormat="dd.MM.yyyy"
                      isClearable
                    />

                    <Button
                      variant="link"
                      className="pe-none position-absolute end-0 top-0"
                      id="button-addon2"
                    >
                      <span className="bi bi-calendar2-event" />
                    </Button>
                  </div>
                  <span className="bi bi-arrow-right" />
                  <div className="w-100 position-relative">
                    <DatePicker
                      locale={fi}
                      selected={
                        feedbacksFilter.FeedbackFetchAnswerEndDate ?? null
                      }
                      onChange={date =>
                        setDatePickerDropdownDateRange(undefined, date ?? null)
                      }
                      selectsEnd
                      startDate={
                        feedbacksFilter.FeedbackFetchAnswerStartDate ?? null
                      }
                      endDate={
                        feedbacksFilter.FeedbackFetchAnswerEndDate ?? null
                      }
                      className="form-control"
                      dateFormat="dd.MM.yyyy"
                      isClearable
                    />

                    <Button
                      variant="link"
                      className="pe-none position-absolute end-0 top-0"
                      id="button-addon2"
                    >
                      <span className="bi bi-calendar2-event" />
                    </Button>
                  </div>
                </div>
              </Col>
              <Col sm="auto" className="d-flex align-items-end">
                <Button onClick={doSearch} type="submit" variant="primary">
                  Hae
                </Button>
              </Col>
            </Row>
          </SearchBarFilters>
        </Card>
        <Loader
          IsLoading={feedbackItemsLoadingStatus !== LOADING_STATUS.IDLE}
        />
        {feedbackItemsLoadingStatus === LOADING_STATUS.IDLE &&
          feedbackItems !== null &&
          feedbackItems.length > 0 && (
            <>
              <DataTable
                HideDeleteButton
                ItemActions={renderItemActions}
                DeleteItem={undefined}
                ColumnWidths={[
                  90, 90, 50, 100, 70, 100, 60, 90, 95, 95, 85, 85, 65,
                ]}
                Header={[
                  'Lähetetty',
                  'Vastattu',
                  'Tila',
                  'Asiakas',
                  'Varaus',
                  'Varaus ajankohta',
                  'Kohde',
                  'Omistaja',
                  'Hyväksytty / Hylätty',
                  'Omistajan palaute',
                  'Käsittelijä',
                  'N8n Päätös',
                  'Toiminnot',
                ]}
                HeaderWithOrderByLinks={HeaderWithOrderByLinks}
                Items={feedbackTableItems}
                LegendText={
                  feedbackTableItems.length === 1 ? 'palaute' : 'palautetta'
                }
                TotalItems={feedbackItemsPagination.totalCount}
                OnHeaderClick={OnHeaderClick}
                SelectedOrderByField={
                  CustomerFeedBackListOrderByOptions[feedbacksFilter.orderBy]
                }
                Ascending={
                  feedbacksFilter.OrderDirection ===
                  CustomerFeedBackListOrderByDirectionOptions.Ascending
                }
              />
              <PaginationComponent
                OnPageClick={HandlePaginationClick}
                PaginationData={feedbackItemsPagination}
              />
            </>
          )}
        {feedbackItemsLoadingStatus === LOADING_STATUS.IDLE &&
          feedbackItems != null &&
          feedbackItems.length < 1 && (
            <p>Ei palautteita annetuilla hakuehdoilla.</p>
          )}
      </Container>

      <Modal show={showN8nModal} onHide={() => setShowN8nModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>n8n Hyväksyntäpäätös</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {n8nError && (
            <div className="alert alert-danger" role="alert">
              <strong>Virhe:</strong> {n8nError}
            </div>
          )}
          {n8nDecision && (
            <div>
              {!n8nDecision.decisionReceived && (
                <div className="alert alert-warning" role="alert">
                  n8n ei vastannut — näytetään viimeksi tallennettu päätös
                </div>
              )}
              <div className="mb-3">
                <strong>Automaattinen hyväksyntä:</strong>{' '}
                <span
                  className={
                    n8nDecision.autoApprove ? 'text-success' : 'text-danger'
                  }
                >
                  {n8nDecision.autoApprove ? 'Kyllä' : 'Ei'}
                </span>
              </div>
              {n8nDecision.reason && (
                <div className="mb-3">
                  <strong>Syy:</strong> {n8nDecision.reason}
                </div>
              )}
              <div className="mb-3">
                <strong>Lähetä sähköposti siivoojalle:</strong>{' '}
                <span
                  className={
                    n8nDecision.sendEmailToCleaner
                      ? 'text-success'
                      : 'text-muted'
                  }
                >
                  {n8nDecision.sendEmailToCleaner ? 'Kyllä' : 'Ei'}
                </span>
              </div>
              <div className="mb-3">
                <strong>Lähetä sähköposti huoltajalle:</strong>{' '}
                <span
                  className={
                    n8nDecision.sendEmailToCaretaker
                      ? 'text-success'
                      : 'text-muted'
                  }
                >
                  {n8nDecision.sendEmailToCaretaker ? 'Kyllä' : 'Ei'}
                </span>
              </div>
            </div>
          )}
          {!n8nError && !n8nDecision && <p>Ladataan...</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowN8nModal(false)}>
            Sulje
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Feedbacks;
